<script setup>
import { computed, onMounted, ref } from 'vue'
import { Badge, Button, EmptyState, PageHeader, Spinner } from '@drama/ui'
import { ArrowLeft } from '@lucide/vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { getLlmRunVideoMatch } from '../../api/llm/index.js'
import { formatDateTime } from './llm-format.js'
import RunVideoMatchGraph from './run-video-match/RunVideoMatchGraph.vue'
import RunVideoMatchInspector from './run-video-match/RunVideoMatchInspector.vue'

const props = defineProps({
  runId: { type: String, required: true },
})

const { t, te } = useI18n()
const route = useRoute()
const router = useRouter()
const loading = ref(false)
const error = ref('')
const detail = ref(null)
const selectedNodeId = ref('')
const selected = ref(null)

const matchedTurns = computed(() => detail.value === null
  ? 0
  : detail.value.turns.filter(turn => turn.context.match_method !== null).length)
const callCount = computed(() => detail.value === null
  ? 0
  : detail.value.turns.reduce((total, turn) => (
      total
      + turn.logs.agent_calls.length
      + turn.logs.embedding_calls.length
      + turn.logs.model_decision_calls.length
    ), 0))

onMounted(load)

async function load() {
  loading.value = true
  error.value = ''
  detail.value = null
  selected.value = null
  selectedNodeId.value = ''
  try {
    detail.value = await getLlmRunVideoMatch(props.runId)
    selectInitialTurn()
  } catch (requestError) {
    error.value = requestError.message
  } finally {
    loading.value = false
  }
}

function selectInitialTurn() {
  if (detail.value.turns.length === 0) return
  const requestedTurnId = typeof route.query.turn_id === 'string' ? route.query.turn_id : null
  const turn = requestedTurnId === null
    ? detail.value.turns[0]
    : detail.value.turns.find(item => item.context.turn_id === requestedTurnId)
  if (turn === undefined) throw new Error(`Run ${props.runId} does not contain turn ${requestedTurnId}`)
  selectedNodeId.value = `turn:${turn.context.turn_id}`
  selected.value = { kind: 'turn', turn }
}

function selectNode(node) {
  selectedNodeId.value = node.id
  selected.value = node.data
}

function back() {
  router.push({ name: 'llm-conversations', query: { q: props.runId } })
}

function runStatusVariant(status) {
  switch (status) {
    case 'active':
      return 'info'
    case 'completed':
      return 'success'
    default:
      return 'neutral'
  }
}

function runStatusLabel(status) {
  const key = `visualGameAdmin.llm.runVideoMatch.runStatus.${status}`
  return te(key) ? t(key) : status
}
</script>

<template>
  <section class="run-video-match-page vga-page">
    <PageHeader :title="t('visualGameAdmin.llm.runVideoMatch.title')">
      <template #actions>
        <Button variant="ghost" @click="back">
          <ArrowLeft :size="16" />
          {{ t('visualGameAdmin.llm.runVideoMatch.back') }}
        </Button>
      </template>
    </PageHeader>

    <div v-if="loading" class="run-video-match-page__state">
      <Spinner size="lg" :label="t('visualGameAdmin.llm.runVideoMatch.loading')" />
    </div>
    <div v-else-if="error" class="run-video-match-page__state">
      <p class="vga-error" role="alert">{{ error }}</p>
      <Button variant="secondary" @click="load">{{ t('visualGameAdmin.common.retry') }}</Button>
    </div>

    <template v-else-if="detail !== null">
      <header class="run-video-match-page__summary">
        <div class="run-video-match-page__identity">
          <div>
            <span>{{ detail.run.scenario_title }}</span>
            <h2>{{ detail.run.id }}</h2>
          </div>
          <Badge :variant="runStatusVariant(detail.run.status)">
            {{ runStatusLabel(detail.run.status) }}
          </Badge>
        </div>
        <dl>
          <div>
            <dt>{{ t('visualGameAdmin.llm.runVideoMatch.turns') }}</dt>
            <dd>{{ detail.run.turn_count }}</dd>
          </div>
          <div>
            <dt>{{ t('visualGameAdmin.llm.runVideoMatch.matchedTurns') }}</dt>
            <dd>{{ matchedTurns }}</dd>
          </div>
          <div>
            <dt>{{ t('visualGameAdmin.llm.runVideoMatch.calls') }}</dt>
            <dd>{{ callCount }}</dd>
          </div>
          <div>
            <dt>{{ t('visualGameAdmin.llm.runVideoMatch.startedAt') }}</dt>
            <dd>{{ formatDateTime(detail.run.created_at) }}</dd>
          </div>
        </dl>
      </header>

      <EmptyState
        v-if="detail.turns.length === 0"
        :title="t('visualGameAdmin.llm.runVideoMatch.empty.title')"
        :description="t('visualGameAdmin.llm.runVideoMatch.empty.description')"
      />

      <div v-else class="run-video-match-page__workspace">
        <RunVideoMatchGraph
          :turns="detail.turns"
          :selected-node-id="selectedNodeId"
          @select="selectNode"
        />
        <RunVideoMatchInspector
          v-if="selected !== null"
          :selected="selected"
          :selected-node-id="selectedNodeId"
          :resources="detail.resources"
          :run-id="detail.run.id"
          @close="selected = null"
        />
      </div>
    </template>
  </section>
</template>

<style scoped>
.run-video-match-page {
  display: grid;
  gap: var(--drama-space-lg);
}

.run-video-match-page__state {
  display: grid;
  min-height: 360px;
  place-items: center;
  align-content: center;
  gap: var(--drama-space-lg);
}

.run-video-match-page__summary {
  display: grid;
  gap: var(--drama-space-lg);
  padding: var(--drama-space-lg);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-lg);
  background: var(--drama-color-surface);
}

.run-video-match-page__identity,
.run-video-match-page__summary dl {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--drama-space-lg);
}

.run-video-match-page__identity span,
.run-video-match-page__summary dt {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.run-video-match-page__identity h2 {
  margin: var(--drama-space-xs) 0 0;
  font-family: var(--drama-font-family-mono);
  font-size: var(--drama-font-size-lg);
  overflow-wrap: anywhere;
}

.run-video-match-page__summary dl {
  justify-content: flex-start;
  margin: 0;
  padding-top: var(--drama-space-md);
  border-top: 1px solid var(--drama-color-border);
}

.run-video-match-page__summary dl div {
  display: grid;
  gap: var(--drama-space-xs);
  min-width: 130px;
}

.run-video-match-page__summary dd {
  margin: 0;
  font-size: var(--drama-font-size-lg);
  font-weight: var(--drama-font-weight-semibold);
}

.run-video-match-page__workspace {
  display: grid;
  grid-template-rows: 580px auto;
  min-height: 0;
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-lg);
  background: var(--drama-color-bg);
  overflow: hidden;
}

@media (max-width: 980px) {
  .run-video-match-page__workspace {
    grid-template-rows: 500px auto;
  }
}

@media (max-width: 680px) {
  .run-video-match-page__identity,
  .run-video-match-page__summary dl {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
